import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import { Item } from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

import { ApplyPreSaleLotteryValue } from '../LotteryDetailForm'

type TicketSelectModalProps = {
  isBeforeReceiption: boolean
  items: Array<Partial<Item>>
  selectedItems: ApplyPreSaleLotteryValue['choices']
  onClose: () => void
  onSelectItem: (item: Partial<Item> | undefined) => void
}

export const TicketSelectModal: Organism<TicketSelectModalProps> = ({
  isBeforeReceiption,
  items,
  selectedItems,
  onClose,
  onSelectItem,
}) => {
  const { t } = useTranslation('lotteryDetail')

  const itemSkus = useMemo(() => {
    return items
      .map(item => {
        return {
          id: item.id,
          title: item.title,
          skuUnitIds: item.skuSettings?.units?.map(unit => unit.id) ?? [],
          skuUnits: item.skuSettings?.units,
          formattedPrice: item.formattedPrice,
          isThroughTicket: item.metadata?.ticket?.isThroughTicket,
        }
      })
      .flat()
  }, [items])

  return (
    <OrganismFrame
      sx={{
        bgcolor: 'background.default',
        height: '75dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" sx={{ py: 4, px: 3 }} alignItems="center">
        <Box width={30} />
        <Typography variant="h6" align="center" width="100%">
          {t('selectTicketTitle', 'チケットを選択')}
        </Typography>
        <IconButton.Close size="small" onClick={onClose} />
      </Stack>
      <Divider />
      <Stack
        flex={1}
        spacing={2}
        sx={{ p: 5, overflowY: 'auto', ...styleSets.hideScrollbar() }}
      >
        {isBeforeReceiption && (
          <Typography color="text.secondary" variant="caption">
            {t(
              'beforeReceiptionNote',
              '受付期間前のためチケットは選択できません。'
            )}
          </Typography>
        )}
        {itemSkus?.map(itemSku => {
          const selectedItemIndexs = selectedItems
            .map((selectedItem, index) => {
              if (selectedItem.itemId === itemSku?.id) {
                return index
              }
            })
            .filter(selectedItemIndex => selectedItemIndex !== undefined)
          const isSelectedItem =
            selectedItemIndexs?.length > 0 &&
            (!itemSku.skuUnitIds.length ||
              selectedItemIndexs.length === itemSku.skuUnitIds.length)
          return (
            <Stack key={itemSku?.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  bgcolor: isSelectedItem ? 'grey.100' : 'grey.50',
                }}
              >
                <CardActionArea
                  disabled={isSelectedItem || isBeforeReceiption}
                  onClick={() =>
                    onSelectItem(items.find(item => item.id === itemSku?.id))
                  }
                >
                  <CardContent
                    sx={{ px: 20, py: 4, '&:last-child': { pb: 4 } }}
                  >
                    <Typography
                      variant="subtitle1"
                      color={
                        isSelectedItem || isBeforeReceiption
                          ? 'text.disabled'
                          : 'text.primary'
                      }
                    >
                      {itemSku?.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {itemSku?.isThroughTicket && (
                        <ThroughTicketTag
                          disabled={isSelectedItem || isBeforeReceiption}
                        />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ pt: 2 }}
                    >
                      <Typography
                        variant="h6"
                        color={
                          isSelectedItem || isBeforeReceiption
                            ? 'text.disabled'
                            : 'text.primary'
                        }
                      >
                        ¥{itemSku?.formattedPrice}
                      </Typography>
                      {isSelectedItem && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Icons.Check fontSize="small" color="primary" />
                          <Typography variant="subtitle1" color="primary.main">
                            {itemSku.skuUnitIds.length
                              ? t('choosing')
                              : t('selectedIndex', {
                                  number: (selectedItemIndexs?.[0] ?? 0) + 1,
                                })}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Stack>
          )
        })}
      </Stack>
    </OrganismFrame>
  )
}
